import { ReactNode } from 'react'

import dayjs from 'utils/dayjs'
import { isPeriodFilterEnum } from 'utils/functions'
import { getParsedDateSpan } from 'utils/dateParser'

import { CustomPeriodFilter, PeriodFilter } from 'types/GlobalWidget'

import { TFunction, useTranslation } from 'language/translator'
import { getDatesOfPeriod } from 'utils/periodFilter'

export type PeriodFilterOption = {
  value: PeriodFilter
  label: ReactNode | string
}

export const usePeriodFilterOptions = (): PeriodFilterOption[] => {
  const { translateEnum } = useTranslation()

  const blockedFilters = [
    PeriodFilter.LATEST_MONTH_WITH_DATA,
    PeriodFilter.LAST_3_FINANCIAL_YEARS_TO_TODAY,
    PeriodFilter.CURRENT_FINANCIAL_YEAR,
    PeriodFilter.PREVIOUS_FINANCIAL_YEAR
  ]

  return [
    ...Object.values(PeriodFilter)
      .filter((period) => !blockedFilters.includes(period))
      .map((value) => ({
        value: value,
        label: translateEnum('period_filters', value).title
      }))
  ]
}

/**
 * Get min date for specific dates.
 * @returns 5 years ago from today.
 */
export const getMinDate = (): string => {
  return dayjs().subtract(5, 'year').startOf('year').format('YYYY-MM-DD')
}

/**
 * Get max date for specific dates.
 *
 * @returns This year and next year.
 */
export const getMaxDate = (): string => {
  return dayjs()
    .add(1, 'year')
    .endOf('year')
    .endOf('month')
    .format('YYYY-MM-DD')
}

export const getPeriodFilterDisplayValue = (
  periodFilter: PeriodFilter | CustomPeriodFilter | null,
  t: TFunction
) => {
  if (!periodFilter) {
    return t('global', 'standard')
  }

  if (isPeriodFilterEnum(periodFilter)) {
    const period = getDatesOfPeriod(periodFilter)

    return getParsedDateSpan(period.from, period.to)
  }

  return getParsedDateSpan(periodFilter.from, periodFilter.to)
}
